import { useEffect, useState } from 'react'

import type { SortingMode } from '@skytech/utils'
import { changeSearchParamByKey, changeSearchParamsPairsByKey, useDebounce } from '@skytech/utils'
import { useLocation, useNavigate } from 'react-router-dom'

export const useSearchParamsByKey = (queryString: string, defaultValue?: string) => {
  const navigate = useNavigate()
  const location = useLocation()

  return changeSearchParamByKey({
    queryString,
    defaultValue,
    search: location.search,
    navigate,
  })
}

export const useSearchParamsPairsByKey = (
  paramPairs: {
    queryString: string
    defaultValue?: string
  }[]
) => {
  const navigate = useNavigate()
  const location = useLocation()

  return changeSearchParamsPairsByKey({ paramPairs, search: location.search, navigate })
}

export const useSortParams = <T extends string>(
  defaultSortingMode?: SortingMode,
  defaultSortById?: T
) => {
  const navigate = useNavigate()
  const location = useLocation()

  const [searchParamPairs, setSearchParamPairs] = changeSearchParamsPairsByKey({
    paramPairs: [
      { queryString: 'sort', defaultValue: defaultSortingMode || 'off' },
      { queryString: 'sortBy', defaultValue: defaultSortById },
    ],
    search: location.search,
    navigate,
  })

  function handleSort(id: T, sortMode: SortingMode) {
    setSearchParamPairs([
      { queryString: 'sort', value: sortMode },
      { queryString: 'sortBy', value: id },
    ])
  }

  return [
    {
      querySort: searchParamPairs.sort.value,
      querySortBy: searchParamPairs.sortBy.value,
    },
    { setSearchParamPairs, handleSort },
  ] as const
}

export function useSearch() {
  const [querySearch, setQuerySearch] = useSearchParamsByKey('search')
  const [searchFilter, setSearchFilter] = useState(querySearch || '')

  const debouncedSearchTerm = useDebounce(searchFilter, 500)

  useEffect(() => {
    setSearchFilter(querySearch || '')
  }, [querySearch])

  useEffect(() => {
    setQuerySearch(debouncedSearchTerm)
  }, [debouncedSearchTerm, setQuerySearch])

  return {
    searchFilter,
    setSearchFilter,
  }
}
